import React, { useState, useEffect } from "react";
import { useUsers } from "../../../hooks/useUsers";
import { useUserAccesses } from "../../../hooks/useUserAccesses";
import { useAccessManager } from "../../../hooks/useAccessManager";
import { useMaterialManager } from "../../../hooks/useMaterialManager";

interface AcessosTabProps {
  // Props específicas se necessário
}

const AcessosTab: React.FC<AcessosTabProps> = () => {
  // Estados locais para a aba de acessos
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const pageSize = 10;

  // Hook para gerenciar usuários
  const { fetchUsers, createUser, updateUser, usersState } = useUsers();

  // Hook para gerenciar acessos do usuário
  const { fetchUserAccesses, clearUserAccesses, userAccessesState } =
    useUserAccesses();

  // Hook para gerenciar acessos
  const { createAccess, updateAccess, deleteAccess } = useAccessManager();

  // Hook para buscar materiais
  const { fetchMaterials, materialState } = useMaterialManager();

  // Estados para o modal de acessos
  const [showUserAccessesModal, setShowUserAccessesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);

  // Estados para criação de acesso
  const [showCreateAccessModal, setShowCreateAccessModal] = useState(false);
  const [showEditAccessModal, setShowEditAccessModal] = useState(false);
  const [editingAccess, setEditingAccess] = useState<any>(null);
  const [newAccessForm, setNewAccessForm] = useState({
    materialId: "",
    expiresAt: "",
    useDefaultDate: true, // Por padrão, usar data padrão do backend
  });

  // Estados para criação e edição de usuário
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: "",
    email: "",
    role: "member" as "admin" | "member",
  });
  const [editUserForm, setEditUserForm] = useState({
    name: "",
    role: "member" as "admin" | "member",
  });

  // Buscar usuários ao montar o componente
  useEffect(() => {
    fetchUsers(searchTerm, currentPage, pageSize);
    fetchMaterials(); // Buscar materiais para o select
  }, [currentPage, searchTerm, fetchMaterials, fetchUsers]);

  // Função para buscar usuários com filtro
  const handleSearchChange = (search: string) => {
    setInputValue(search);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(inputValue);
    setCurrentPage(1); // Reset para primeira página ao buscar
  };

  // Função para abrir modal de acessos do usuário
  const handleUserClick = async (user: {
    id: string;
    name: string;
    email: string;
  }) => {
    setSelectedUser(user);
    setShowUserAccessesModal(true);
    try {
      await fetchUserAccesses(user.id);
    } catch (error) {}
  };

  // Função para fechar modal de acessos
  const handleCloseUserAccessesModal = () => {
    setShowUserAccessesModal(false);
    setSelectedUser(null);
    clearUserAccesses();
    setShowCreateAccessModal(false);
    setShowEditAccessModal(false);
    setEditingAccess(null);
    setNewAccessForm({ materialId: "", expiresAt: "", useDefaultDate: true });
  };

  // Função para criar novo acesso
  const handleCreateAccess = async () => {
    if (!selectedUser || !newAccessForm.materialId) {
      alert("Selecione um material");
      return;
    }

    // Se usar data padrão, calcular 100 anos a partir de hoje
    let expiresAtValue: string;
    if (newAccessForm.useDefaultDate) {
      // Calcular 100 anos a partir de hoje
      const defaultDate = new Date();
      defaultDate.setFullYear(defaultDate.getFullYear() + 100);
      // Formatar como YYYY-MM-DD (formato ISO date esperado pelo backend)
      expiresAtValue = defaultDate.toISOString().split("T")[0];
    } else {
      if (!newAccessForm.expiresAt) {
        alert(
          "Selecione uma data de expiração ou marque para usar a data padrão"
        );
        return;
      }
      // O input já retorna no formato YYYY-MM-DD
      expiresAtValue = newAccessForm.expiresAt;
    }

    try {
      await createAccess({
        userId: selectedUser.id,
        materialId: newAccessForm.materialId,
        expiresAt: expiresAtValue,
      });

      // Recarregar acessos do usuário
      await fetchUserAccesses(selectedUser.id);

      setShowCreateAccessModal(false);
      setNewAccessForm({ materialId: "", expiresAt: "", useDefaultDate: true });
    } catch (error) {
      alert("Erro ao criar acesso");
    }
  };

  // Função para editar acesso
  const handleEditAccess = (access: any) => {
    setEditingAccess(access);
    setShowEditAccessModal(true);
  };

  // Função para atualizar acesso
  const handleUpdateAccess = async () => {
    if (!editingAccess) return;

    try {
      await updateAccess(editingAccess.id, {
        expiresAt: editingAccess.expiresAt,
      });

      // Recarregar acessos do usuário
      if (selectedUser) {
        await fetchUserAccesses(selectedUser.id);
      }

      setShowEditAccessModal(false);
      setEditingAccess(null);
    } catch (error) {
      alert("Erro ao atualizar acesso");
    }
  };

  // Função para deletar acesso
  const handleDeleteAccess = async (accessId: string) => {
    if (!window.confirm("Tem certeza que deseja deletar este acesso?")) return;

    try {
      await deleteAccess(accessId);

      // Recarregar acessos do usuário
      if (selectedUser) {
        await fetchUserAccesses(selectedUser.id);
      }
    } catch (error) {
      alert("Erro ao deletar acesso");
    }
  };

  // Função para abrir modal de criação de usuário
  const handleOpenCreateUserModal = () => {
    setNewUserForm({ name: "", email: "", role: "member" });
    setShowCreateUserModal(true);
  };

  // Função para criar usuário
  const handleCreateUser = async () => {
    if (!newUserForm.name.trim() || !newUserForm.email.trim()) {
      alert("Preencha todos os campos");
      return;
    }

    try {
      await createUser(newUserForm.name, newUserForm.email, newUserForm.role);
      alert("Usuário criado com sucesso!");
      setShowCreateUserModal(false);
      setNewUserForm({ name: "", email: "", role: "member" });
      // Recarregar lista de usuários
      await fetchUsers(searchTerm, currentPage, pageSize);
    } catch (error) {
      alert(error instanceof Error ? error.message : "Erro ao criar usuário");
    }
  };

  // Função para abrir modal de edição de usuário
  const handleOpenEditUserModal = () => {
    if (!selectedUser) return;

    // Buscar dados atuais do usuário
    const currentUser = usersState.users.find((u) => u.id === selectedUser.id);
    if (currentUser) {
      setEditUserForm({
        name: currentUser.name,
        role: currentUser.role,
      });
    }
    setShowEditUserModal(true);
  };

  // Função para editar usuário
  const handleEditUser = async () => {
    if (!selectedUser || !editUserForm.name.trim()) {
      alert("Preencha o nome");
      return;
    }

    try {
      await updateUser(selectedUser.id, editUserForm.name, editUserForm.role);
      alert("Usuário atualizado com sucesso!");
      setShowEditUserModal(false);
      // Recarregar lista de usuários
      await fetchUsers(searchTerm, currentPage, pageSize);
      // Atualizar dados do usuário selecionado
      setSelectedUser({
        ...selectedUser,
        name: editUserForm.name,
      });
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Erro ao atualizar usuário"
      );
    }
  };

  // Paginação
  const totalPages = Math.ceil(usersState.totalCount / pageSize);
  const getPaginatedUsers = () => {
    return usersState.users;
  };

  // Loading state
  if (usersState.isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <span>Carregando usuários...</span>
      </div>
    );
  }

  // Error state
  if (usersState.error) {
    return <div className="error-message">❌ {usersState.error}</div>;
  }

  return (
    <div className="acessos-container">
      {/* Cabeçalho da aba */}
      <div className="acessos-header">
        <h3>👥 Gerenciar Acessos</h3>
        <p>Visualize e gerencie todos os usuários cadastrados no sistema.</p>
      </div>

      {/* Barra de busca */}
      <div className="search-section">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
            className="search-input"
          />
          <span className="search-icon" onClick={handleSearchSubmit}>
            🔍
          </span>
        </div>
        <button
          className="create-user-button"
          onClick={handleOpenCreateUserModal}
          title="Criar novo usuário"
        >
          ➕ Criar Usuário
        </button>
      </div>

      {/* Estatísticas */}
      <div className="stats-section">
        <div className="stat-card">
          <span className="stat-number">{usersState.totalCount}</span>
          <span className="stat-label">Total de Usuários</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">
            {usersState.users.filter((user) => user.role === "admin").length}
          </span>
          <span className="stat-label">Administradores</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">
            {usersState.users.filter((user) => user.role === "member").length}
          </span>
          <span className="stat-label">Membros</span>
        </div>
      </div>

      {/* Lista de Usuários */}
      <div className="users-list-section">
        <h4>📋 Lista de Usuários</h4>

        {usersState.users.length === 0 ? (
          <div className="no-users-message">
            <p>Nenhum usuário encontrado.</p>
            {searchTerm && <p>Tente ajustar os termos de busca.</p>}
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Data de Cadastro</th>
                  <th>Última Atualização</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedUsers().map((user) => (
                  <tr
                    key={user.id}
                    className="user-row user-row-clickable"
                    onClick={() =>
                      handleUserClick({
                        id: user.id,
                        name: user.name,
                        email: user.email,
                      })
                    }
                    title="Clique para ver os acessos deste usuário"
                  >
                    <td className="user-info">
                      <div className="user-info-content">
                        <div className="user-avatar">👤</div>
                        <div className="user-details">
                          <span className="user-name">{user.name}</span>
                        </div>
                      </div>
                    </td>
                    <td className="user-email">
                      <span className="email-text">{user.email}</span>
                    </td>
                    <td className="user-role">
                      <span className="role-text">
                        {user.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="user-date">
                      {new Date(user.createdAt).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="user-updated">
                      {new Date(user.updatedAt).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="users-pagination">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  ← Anterior
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages}({usersState.totalCount}{" "}
                  usuários)
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Próximo →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Acessos do Usuário */}
      {showUserAccessesModal && selectedUser && (
        <div
          className="modal-overlay user-accesses-overlay"
          onClick={handleCloseUserAccessesModal}
        >
          <div
            id="user-accesses-modal"
            className="modal user-accesses-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Acessos de {selectedUser.name}</h3>
              <div className="modal-header-actions">
                <button
                  className="edit-user-button"
                  onClick={handleOpenEditUserModal}
                  title="Editar usuário"
                >
                  ✏️ Editar Usuário
                </button>
                <button
                  className="add-access-button"
                  onClick={() => setShowCreateAccessModal(true)}
                  title="Adicionar novo acesso"
                >
                  ➕
                </button>
                <button
                  className="close-button"
                  onClick={handleCloseUserAccessesModal}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="user-accesses-info">
                <p>
                  <strong>Email:</strong> {selectedUser.email}
                </p>
              </div>

              {userAccessesState.isLoading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <span>Carregando acessos...</span>
                </div>
              ) : userAccessesState.error ? (
                <div className="error-message">
                  ❌ {userAccessesState.error}
                </div>
              ) : userAccessesState.accesses.length === 0 ? (
                <div className="no-accesses-message">
                  <p>Este usuário não possui acessos a materiais.</p>
                </div>
              ) : (
                <div className="user-accesses-table-container">
                  <table className="user-accesses-table">
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th>ID de Referência</th>
                        <th>Data de Concessão</th>
                        <th>Data de Expiração</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userAccessesState.accesses.map((access) => (
                        <tr key={access.id} className="user-access-row">
                          <td className="access-material">
                            <div className="material-info">
                              <span className="material-title">
                                {access.material?.title ||
                                  "Material não encontrado"}
                              </span>
                              {access.material?.description && (
                                <span className="material-description">
                                  {access.material.description}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="access-reference-id">
                            <span className="reference-id-value">
                              {access.material?.referenceIds &&
                              access.material.referenceIds.length > 0
                                ? access.material.referenceIds.join(", ")
                                : access.material?.referenceId || "N/A"}
                            </span>
                          </td>
                          <td className="access-created">
                            {new Date(access.createdAt).toLocaleDateString(
                              "pt-BR",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </td>
                          <td className="access-expires">
                            {new Date(access.expiresAt).toLocaleDateString(
                              "pt-BR",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </td>
                          <td className="access-status">
                            <span
                              className={`status-badge ${
                                new Date(access.expiresAt) > new Date()
                                  ? "active"
                                  : "expired"
                              }`}
                            >
                              {new Date(access.expiresAt) > new Date()
                                ? "🟢 Ativo"
                                : "🔴 Expirado"}
                            </span>
                          </td>
                          <td className="access-actions">
                            <button
                              className="edit-access-button"
                              onClick={() => handleEditAccess(access)}
                              title="Editar acesso"
                            >
                              ⚙️
                            </button>
                            <button
                              className="delete-access-button"
                              onClick={() => handleDeleteAccess(access.id)}
                              title="Deletar acesso"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para criar novo acesso */}
      {showCreateAccessModal && selectedUser && (
        <div
          className="modal-overlay create-access-overlay"
          onClick={() => setShowCreateAccessModal(false)}
        >
          <div
            className="modal create-access-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                Adicionar Acesso - {selectedUser.name}
              </h3>
              <button
                className="close-button"
                onClick={() => setShowCreateAccessModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Material:</label>
                <select
                  value={newAccessForm.materialId}
                  onChange={(e) =>
                    setNewAccessForm((prev) => ({
                      ...prev,
                      materialId: e.target.value,
                    }))
                  }
                  className="form-select"
                >
                  <option value="">Selecione um material</option>
                  {materialState.materials.map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.title} (ID:{" "}
                      {material.referenceIds?.join(", ") || "N/A"})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newAccessForm.useDefaultDate}
                    onChange={(e) =>
                      setNewAccessForm((prev) => ({
                        ...prev,
                        useDefaultDate: e.target.checked,
                        expiresAt: e.target.checked ? "" : prev.expiresAt,
                      }))
                    }
                    className="form-checkbox"
                    style={{ marginRight: "8px" }}
                  />
                  Usar data padrão do sistema
                </label>
                {!newAccessForm.useDefaultDate && (
                  <>
                    <label style={{ marginTop: "12px", display: "block" }}>
                      Data de Expiração:
                    </label>
                    <input
                      type="date"
                      value={newAccessForm.expiresAt}
                      onChange={(e) =>
                        setNewAccessForm((prev) => ({
                          ...prev,
                          expiresAt: e.target.value,
                        }))
                      }
                      className="form-input"
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </>
                )}
              </div>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCreateAccessModal(false)}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateAccess}
                >
                  Criar Acesso
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar acesso */}
      {showEditAccessModal && editingAccess && (
        <div
          className="modal-overlay edit-access-overlay"
          onClick={() => setShowEditAccessModal(false)}
        >
          <div
            className="modal edit-access-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Editar Acesso</h3>
              <button
                className="close-button"
                onClick={() => setShowEditAccessModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Material:</label>
                <input
                  type="text"
                  value={
                    editingAccess.material?.title || "Material não encontrado"
                  }
                  className="form-input"
                  disabled
                />
                <small>
                  IDs:{" "}
                  {editingAccess.material?.referenceIds &&
                  editingAccess.material.referenceIds.length > 0
                    ? editingAccess.material.referenceIds.join(", ")
                    : editingAccess.material?.referenceId || "N/A"}
                </small>
              </div>
              <div className="form-group">
                <label>Data de Expiração:</label>
                <input
                  type="date"
                  value={
                    editingAccess.expiresAt
                      ? editingAccess.expiresAt.split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setEditingAccess((prev: any) => ({
                      ...prev,
                      expiresAt: e.target.value,
                    }))
                  }
                  className="form-input"
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEditAccessModal(false)}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateAccess}
                >
                  Atualizar Acesso
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para criar usuário */}
      {showCreateUserModal && (
        <div
          className="modal-overlay create-user-overlay"
          onClick={() => setShowCreateUserModal(false)}
        >
          <div
            id="create-user-modal"
            className="modal create-user-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Criar Novo Usuário</h3>
              <button
                className="close-button"
                onClick={() => setShowCreateUserModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nome:</label>
                <input
                  type="text"
                  value={newUserForm.name}
                  onChange={(e) =>
                    setNewUserForm({ ...newUserForm, name: e.target.value })
                  }
                  className="form-input"
                  placeholder="Nome completo"
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) =>
                    setNewUserForm({ ...newUserForm, email: e.target.value })
                  }
                  className="form-input"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="form-group">
                <label>Role:</label>
                <select
                  value={newUserForm.role}
                  onChange={(e) =>
                    setNewUserForm({
                      ...newUserForm,
                      role: e.target.value as "admin" | "member",
                    })
                  }
                  className="form-input"
                >
                  <option value="member">Member (Membro)</option>
                  <option value="admin">Admin (Administrador)</option>
                </select>
                <small>
                  Membros: acesso básico | Administradores: acesso total
                </small>
              </div>
              <div className="form-info">
                <p>
                  ℹ️ A senha padrão será: <strong>123456</strong>
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateUserModal(false)}
              >
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleCreateUser}>
                Criar Usuário
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar usuário */}
      {showEditUserModal && selectedUser && (
        <div
          className="modal-overlay edit-user-overlay"
          onClick={() => setShowEditUserModal(false)}
        >
          <div
            id="edit-user-modal"
            className="modal edit-user-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Editar Usuário</h3>
              <button
                className="close-button"
                onClick={() => setShowEditUserModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={selectedUser.email}
                  className="form-input"
                  disabled
                />
                <small>O email não pode ser alterado</small>
              </div>
              <div className="form-group">
                <label>Nome:</label>
                <input
                  type="text"
                  value={editUserForm.name}
                  onChange={(e) =>
                    setEditUserForm({ ...editUserForm, name: e.target.value })
                  }
                  className="form-input"
                  placeholder="Nome completo"
                />
              </div>
              <div className="form-group">
                <label>Role:</label>
                <select
                  value={editUserForm.role}
                  onChange={(e) =>
                    setEditUserForm({
                      ...editUserForm,
                      role: e.target.value as "admin" | "member",
                    })
                  }
                  className="form-input"
                >
                  <option value="member">Member (Membro)</option>
                  <option value="admin">Admin (Administrador)</option>
                </select>
                <small>
                  Membros: acesso básico | Administradores: acesso total
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditUserModal(false)}
              >
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleEditUser}>
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcessosTab;
